import { useState } from 'react';
import { useSelector } from 'react-redux';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useToast } from '../../../hooks';

export default function NotificationsTab() {
  const { showToast } = useToast();
  const { user } = useSelector((state) => state.auth);

  const [settings, setSettings] = useState({
    // Email Notifications
    emailEnabled: user?.notificationSettings?.emailEnabled ?? true,
    emailFrequency: user?.notificationSettings?.emailFrequency || 'instant',
    emailTime: user?.notificationSettings?.emailTime || '09:00',
    
    // In-App Notifications
    inAppEnabled: user?.notificationSettings?.inAppEnabled ?? true,
    
    // Desktop Notifications
    desktopEnabled: user?.notificationSettings?.desktopEnabled ?? false,
    
    // Notification Types
    notificationTypes: {
      inventoryAlerts: user?.notificationSettings?.notificationTypes?.inventoryAlerts ?? true,
      budgetAlerts: user?.notificationSettings?.notificationTypes?.budgetAlerts ?? true,
      taskAssignments: user?.notificationSettings?.notificationTypes?.taskAssignments ?? true,
      approvalRequests: user?.notificationSettings?.notificationTypes?.approvalRequests ?? true,
      systemUpdates: user?.notificationSettings?.notificationTypes?.systemUpdates ?? true,
      reportsReady: user?.notificationSettings?.notificationTypes?.reportsReady ?? true,
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const handleToggle = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationTypeToggle = (type) => {
    setSettings((prev) => ({
      ...prev,
      notificationTypes: {
        ...prev.notificationTypes,
        [type]: !prev.notificationTypes[type],
      },
    }));
  };

  const handleFrequencyChange = (e) => {
    setSettings((prev) => ({ ...prev, emailFrequency: e.target.value }));
  };

  const handleTimeChange = (e) => {
    setSettings((prev) => ({ ...prev, emailTime: e.target.value }));
  };

  const requestDesktopPermission = async () => {
    if (typeof Notification === 'undefined') {
      showToast('Desktop notifications are not supported in your browser', 'error');
      return;
    }

    if (Notification.permission === 'granted') {
      setSettings((prev) => ({ ...prev, desktopEnabled: true }));
      setPermissionStatus('granted');
      showToast('Desktop notifications enabled', 'success');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        setSettings((prev) => ({ ...prev, desktopEnabled: true }));
        showToast('Desktop notifications enabled', 'success');
        
        // Show test notification
        new Notification('ERP System', {
          body: 'Desktop notifications are now enabled!',
          icon: '/favicon.ico',
        });
      } else {
        showToast('Desktop notification permission denied', 'error');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      showToast('Failed to request notification permission', 'error');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: API call to save notification settings
      // await updateNotificationSettings(settings);
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      showToast('Notification preferences saved successfully', 'success');
      
      // Update Redux store
      // dispatch(updateNotificationSettings(settings));
    } catch (error) {
      showToast('Failed to save notification preferences', 'error');
      console.error('Error saving notification settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      emailEnabled: true,
      emailFrequency: 'instant',
      emailTime: '09:00',
      inAppEnabled: true,
      desktopEnabled: false,
      notificationTypes: {
        inventoryAlerts: true,
        budgetAlerts: true,
        taskAssignments: true,
        approvalRequests: true,
        systemUpdates: true,
        reportsReady: true,
      },
    });
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(user?.notificationSettings || {});

  const notificationTypesList = [
    { key: 'inventoryAlerts', label: 'Inventory Alerts', description: 'Low stock, out of stock, and reorder notifications' },
    { key: 'budgetAlerts', label: 'Budget Alerts', description: 'Overspending warnings and budget limit notifications' },
    { key: 'taskAssignments', label: 'Task Assignments', description: 'When you are assigned a new task or task status changes' },
    { key: 'approvalRequests', label: 'Approval Requests', description: 'Requests requiring your approval or review' },
    { key: 'systemUpdates', label: 'System Updates', description: 'Important system announcements and maintenance notices' },
    { key: 'reportsReady', label: 'Reports Ready', description: 'When scheduled or requested reports are available' },
  ];

  return (
    <div className="max-w-3xl space-y-8">
      {/* Email Notifications */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
            <p className="text-sm text-gray-500">Receive notifications via email</p>
          </div>
          <button
            onClick={() => handleToggle('emailEnabled', !settings.emailEnabled)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${settings.emailEnabled ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${settings.emailEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {settings.emailEnabled && (
          <div className="space-y-4 pl-4 border-l-2 border-gray-200">
            <div>
              <label htmlFor="emailFrequency" className="block text-sm font-medium text-gray-700 mb-2">
                Email Frequency
              </label>
              <select
                id="emailFrequency"
                value={settings.emailFrequency}
                onChange={handleFrequencyChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="instant">Instant (as they happen)</option>
                <option value="hourly">Hourly Digest</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Summary</option>
              </select>
            </div>

            {(settings.emailFrequency === 'daily' || settings.emailFrequency === 'weekly') && (
              <div>
                <label htmlFor="emailTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time
                </label>
                <input
                  type="time"
                  id="emailTime"
                  value={settings.emailTime}
                  onChange={handleTimeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {settings.emailFrequency === 'daily' 
                    ? 'Daily digest will be sent at this time'
                    : 'Weekly summary will be sent on Mondays at this time'
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-8">
        {/* In-App Notifications */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">In-App Notifications</h3>
            <p className="text-sm text-gray-500">Show notifications within the ERP system</p>
          </div>
          <button
            onClick={() => handleToggle('inAppEnabled', !settings.inAppEnabled)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${settings.inAppEnabled ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${settings.inAppEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-8">
        {/* Desktop Notifications */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Desktop Notifications</h3>
            <p className="text-sm text-gray-500">Show browser notifications even when ERP is in background</p>
          </div>
          <div className="flex items-center gap-3">
            {permissionStatus === 'denied' && (
              <span className="text-xs text-red-600">Permission denied</span>
            )}
            <button
              onClick={() => {
                if (!settings.desktopEnabled && permissionStatus !== 'granted') {
                  requestDesktopPermission();
                } else {
                  handleToggle('desktopEnabled', !settings.desktopEnabled);
                }
              }}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${settings.desktopEnabled ? 'bg-blue-600' : 'bg-gray-200'}
              `}
              disabled={permissionStatus === 'denied'}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${settings.desktopEnabled ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>

        {permissionStatus === 'denied' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AppIcon name="AlertTriangle" className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="flex-1 text-sm text-yellow-800">
                <p className="font-medium mb-1">Desktop notifications blocked</p>
                <p>
                  You've blocked desktop notifications for this site. To enable them, please update your browser settings.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-8">
        {/* Notification Types */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Notification Types</h3>
          <p className="text-sm text-gray-500">Choose which types of notifications you want to receive</p>
        </div>

        <div className="space-y-4">
          {notificationTypesList.map((type) => (
            <div
              key={type.key}
              className="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1">
                <label htmlFor={type.key} className="block text-sm font-medium text-gray-900 cursor-pointer">
                  {type.label}
                </label>
                <p className="text-xs text-gray-500 mt-1">{type.description}</p>
              </div>
              <button
                id={type.key}
                onClick={() => handleNotificationTypeToggle(type.key)}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4
                  ${settings.notificationTypes[type.key] ? 'bg-blue-600' : 'bg-gray-300'}
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${settings.notificationTypes[type.key] ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <Button
          variant="ghost"
          onClick={handleReset}
          className="text-gray-600"
        >
          Reset to Defaults
        </Button>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <>
                <AppIcon name="Loader2" className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <AppIcon name="Check" className="w-4 h-4" />
                Save Preferences
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
